import { useMemo, useState } from "react";

/** Asked before an unsaved editor is thrown away. */
const DISCARD_PROMPT = "Discard your unsaved pickup service changes?";

import { Plus } from "lucide-react";

import {
  type CarrierProviderAddOnService,
  useCarrierProviderAddOnServices,
} from "@/api/carrier";
import { PickupServiceConfig } from "@/features/pickup-service-config";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreatePickupServiceDialog } from "@/features/create-pickup-service-dialog";
import {
  PickupServiceCatalogue,
  PickupServicesPermissionState,
} from "@/features/pickup-service-catalog";
import { hasHttpStatus } from "@/lib/api-error";

type AvailabilityFilter = "ALL" | "ACTIVE" | "INACTIVE";

export default function PickupServicesScreen() {
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [availability, setAvailability] = useState<AvailabilityFilter>("ALL");
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState<CarrierProviderAddOnService | null>(
    null
  );
  const services = useCarrierProviderAddOnServices({
    type: "PICKUP",
    all: true,
  });
  const rows: CarrierProviderAddOnService[] = Array.isArray(services.data)
    ? services.data
    : (services.data?.data ?? []);
  const filteredRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return rows.filter((service) => {
      const matchesQuery =
        !query ||
        service.name.toLocaleLowerCase().includes(query) ||
        service.code.toLocaleLowerCase().includes(query) ||
        service.provider.name.toLocaleLowerCase().includes(query);
      const matchesAvailability =
        availability === "ALL" ||
        (availability === "ACTIVE" ? service.active : !service.active);
      return matchesQuery && matchesAvailability;
    });
  }, [availability, rows, search]);

  const forbidden = hasHttpStatus(services.error, 403);
  const initialLoading = services.isLoading && rows.length === 0;
  const hasFilters = search.trim() !== "" || availability !== "ALL";
  const countLabel = `${rows.length} pickup service${rows.length === 1 ? "" : "s"}`;

  return (
    <div className="carrier-page">
      <PageHeader
        title="Pickup Services"
        actions={
          forbidden ? null : (
            <Button size="touch" onClick={() => setCreating(true)}>
              <Plus className="mr-2 h-4 w-4" /> Add pickup service
            </Button>
          )
        }
      />

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
          <div>
            <h2 className="font-semibold">Pickup services</h2>
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {initialLoading ? "Loading…" : countLabel}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              type="search"
              aria-label="Search pickup services"
              placeholder="Search services…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="sm:w-56"
            />
            <Select
              value={availability}
              onValueChange={(value) =>
                setAvailability(value as AvailabilityFilter)
              }
            >
              <SelectTrigger
                aria-label="Filter by availability"
                size="sm"
                className="w-full sm:w-40"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All availability</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {forbidden ? (
          <PickupServicesPermissionState />
        ) : (
          <>
            {services.error && (
              <div
                role="alert"
                className="flex flex-col gap-3 border-b bg-destructive/5 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <span>Pickup services could not be refreshed.</span>
                <Button
                  variant="outline"
                  size="sm-touch"
                  onClick={() => void services.refetch()}
                >
                  Try again
                </Button>
              </div>
            )}
            {(!services.error || rows.length > 0) && (
              <PickupServiceCatalogue
                rows={filteredRows}
                initialLoading={initialLoading}
                hasFilters={hasFilters}
                onView={setSelected}
              />
            )}
          </>
        )}
      </Card>

      {selected && (
        <section
          aria-label={`${selected.name} configuration`}
          className="space-y-3"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold">{selected.name}</h2>
              <p className="text-xs text-muted-foreground">
                {selected.provider.name} · {selected.code}
              </p>
            </div>
            <span className="flex items-center gap-2">
              {dirty && (
                <span className="text-xs font-medium text-amber-700">
                  Unsaved changes
                </span>
              )}
              <Button
                variant="ghost"
                size="sm-touch"
                onClick={() => {
                  if (dirty && !window.confirm(DISCARD_PROMPT)) return;
                  setDirty(false);
                  setSelected(null);
                }}
              >
                Close
              </Button>
            </span>
          </div>
          <PickupServiceConfig
            key={selected.id}
            service={selected}
            onDirtyChange={setDirty}
          />
        </section>
      )}

      <CreatePickupServiceDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}
